import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { sendMail } from "../utils/sendMail";

const MAX_DOC_BYTES = 40 * 1024; // 40 KB — PAN card / Aadhar card scans

export default function RegisterPage({ navigate }) {
  const { register } = useAuth();
  const [f, setF] = useState({
    name:"",email:"",agency:"",password:"",confirm:"",
    pan_number:"",pan_card:"",pan_card_name:"",
    aadhar_number:"",aadhar_card:"",aadhar_card_name:"",
  });
  const [error,   setError]   = useState(null);
  const [success, setSuccess] = useState(false);
  const s = k => e => setF(p=>({...p,[k]:e.target.value}));

  const upperPan = e => setF(p=>({...p, pan_number: e.target.value.toUpperCase().slice(0,10)}));
  const digitsOnlyAadhar = e => setF(p=>({...p, aadhar_number: e.target.value.replace(/\D/g,"").slice(0,12)}));

  const makeFileHandler = (field, nameField) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Please upload a valid image file (JPG/PNG)."); e.target.value=""; return; }
    if (file.size > MAX_DOC_BYTES) { setError(`File too large (${(file.size/1024).toFixed(1)} KB). Max allowed size is 40 KB.`); e.target.value=""; return; }
    const reader = new FileReader();
    reader.onload = () => { setF(p=>({...p, [field]:reader.result, [nameField]:file.name})); setError(null); };
    reader.readAsDataURL(file);
    e.target.value = "";
  };
  const handlePan    = makeFileHandler("pan_card","pan_card_name");
  const handleAadhar = makeFileHandler("aadhar_card","aadhar_card_name");

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name||!f.email||!f.agency||!f.password) return setError("All fields are required.");
    if (f.password !== f.confirm)    return setError("Passwords do not match.");
    if (f.password.length < 6)       return setError("Password must be at least 6 characters.");
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(f.pan_number))
      return setError("Enter a valid PAN number (format: ABCDE1234F).");
    if (!f.pan_card)
      return setError("PAN card upload is required.");
    if (f.aadhar_number.length !== 12)
      return setError("Enter a valid 12-digit Aadhar number.");
    if (!f.aadhar_card)
      return setError("Aadhar card upload is required.");

    const err = await register(f.name, f.email, f.agency, f.password, {
      pan_number: f.pan_number, pan_card: f.pan_card, pan_card_name: f.pan_card_name,
      aadhar_number: f.aadhar_number, aadhar_card: f.aadhar_card, aadhar_card_name: f.aadhar_card_name,
    });
    if (err) return setError(err);

    const adminEmail = (window.EMAILJS_CONFIG && window.EMAILJS_CONFIG.adminEmail) || "airhotelvisa@gmail.com";
    sendMail({
      to_email: adminEmail,
      to_name:  "Admin",
      subject:  "New Agent Registration — Approval Needed",
      message:  `${f.name} (${f.agency}) has registered as a new agent — email: ${f.email}. Please review and approve their account in the Admin Panel before they can login.`,
    });
    sendMail({
      to_email: f.email,
      to_name:  f.name,
      subject:  "Registration Received — Pending Admin Approval",
      message:  `Hi ${f.name}, thanks for registering with AIHOVI B2B. Your agent account has been created and is pending admin approval. You'll be able to login once an admin approves your account.`,
    });

    setSuccess(true); setError(null);
  };

  const iStyle = {width:"100%",padding:"11px 14px",border:"1.5px solid #dde3ed",borderRadius:9,fontSize:13,fontFamily:"'DM Sans',sans-serif",color:"#0d1f3c",background:"#fafbfd",outline:"none",boxSizing:"border-box"};
  const lbl = (t) => <label style={{display:"block",fontSize:11,fontWeight:600,letterSpacing:"1.5px",textTransform:"uppercase",color:"#5a6a80",marginBottom:6}}>{t}</label>;

  const UploadField = ({label, value, name, onChange, hint}) => (
    <div style={{gridColumn:"1/-1"}}>
      {lbl(label)}
      <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
        {value ? (
          <div style={{display:"flex",alignItems:"center",gap:10,background:"#f6f8fc",border:"1px solid #dde3ed",borderRadius:10,padding:"8px 12px"}}>
            <img src={value} alt={label} style={{width:44,height:44,objectFit:"cover",borderRadius:8,border:"1px solid #dde3ed"}}/>
            <div style={{fontSize:12,fontWeight:600,color:"#1a2540"}}>{name}<div style={{fontSize:11,color:"#16a34a",fontWeight:400}}>✓ Uploaded</div></div>
          </div>
        ) : (
          <label style={{display:"inline-flex",alignItems:"center",gap:8,padding:"10px 16px",background:"#f6f8fc",border:"1.5px dashed #b8c4d8",borderRadius:10,fontSize:12,fontWeight:600,color:"#4a7fb5",cursor:"pointer"}}>
            📄 Choose File
            <input type="file" accept="image/*" onChange={onChange} style={{display:"none"}}/>
          </label>
        )}
      </div>
      <div style={{fontSize:11,color:"#8a9bb5",marginTop:6}}>{hint}</div>
    </div>
  );

  return (
    <div style={{minHeight:"100vh",background:"#f0f3f9",display:"flex",alignItems:"center",justifyContent:"center",padding:"28px 20px",fontFamily:"'DM Sans',sans-serif"}}>
      <style>{`
        @media (max-width:560px){
          .reg-card{padding:30px 22px !important;}
          .reg-grid{grid-template-columns:1fr !important;}
        }
      `}</style>
      <div className="reg-card" style={{background:"#fff",borderRadius:20,padding:"40px 42px",maxWidth:560,width:"100%",boxShadow:"0 8px 40px rgba(10,22,40,0.1)",border:"1px solid #e4e9f0",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:0,left:0,right:0,height:4,background:"linear-gradient(135deg,#4a7fb5,#d4736a)"}}/>

        <div style={{textAlign:"center",marginBottom:22}}>
          <img src="/logo-small.png" alt="AIHOVI B2B" style={{height:74,objectFit:"contain"}} onError={e=>e.target.style.display="none"}/>
        </div>

        <h1 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:28,fontWeight:700,color:"#0d1f3c",textAlign:"center",marginBottom:5}}>Agent Registration</h1>
        <p style={{fontSize:13,color:"#6b7a90",textAlign:"center",marginBottom:22,lineHeight:1.6}}>Register as an authorized AIHOVI B2B travel agent.</p>

        {error && <div style={{background:"#fee2e2",border:"1px solid #ef4444",color:"#991b1b",padding:"12px 16px",borderRadius:10,fontSize:13,marginBottom:18}}>⚠ {error}</div>}

        {success ? (
          <div style={{textAlign:"center"}}>
            <div style={{background:"#dcfce7",border:"1px solid #22c55e",color:"#166534",padding:20,borderRadius:10,fontSize:13,marginBottom:20,lineHeight:1.8}}>
              ✅ <strong>Registration Successful!</strong><br/>Your agent account is created and is pending admin approval. You will be able to login once an admin approves your account.
            </div>
            <button onClick={()=>navigate("login")} style={{padding:"12px 32px",background:"linear-gradient(135deg,#4a7fb5,#d4736a)",color:"#fff",border:"none",borderRadius:10,fontSize:14,fontWeight:600,cursor:"pointer"}}>→ Go to Login</button>
          </div>
        ) : (
          <>
            <div style={{background:"#f4f7ff",borderRadius:10,padding:"11px 15px",fontSize:12,color:"#5a6a80",lineHeight:1.6,marginBottom:18}}>
              <strong style={{color:"#0d1f3c"}}>Note:</strong> Authorized travel agencies only. PAN &amp; Aadhar verification is required for KYC compliance. Your account will be created with <strong>Pending</strong> status — an admin must approve it before you can login.
            </div>
            <form onSubmit={submit} noValidate>
              <div className="reg-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                <div>
                  {lbl("Full Name *")}
                  <input type="text" value={f.name} onChange={s("name")} placeholder="Enter full name" style={iStyle} required/>
                </div>
                <div>
                  {lbl("Email Address *")}
                  <input type="email" value={f.email} onChange={s("email")} placeholder="agent@agency.com" style={iStyle} required/>
                </div>
                <div style={{gridColumn:"1/-1"}}>
                  {lbl("Agency / Company *")}
                  <input type="text" value={f.agency} onChange={s("agency")} placeholder="e.g. TravelPro India Pvt. Ltd." style={iStyle} required/>
                </div>
                <div>
                  {lbl("Password *")}
                  <input type="password" value={f.password} onChange={s("password")} placeholder="Min 6 characters" style={iStyle} required/>
                </div>
                <div>
                  {lbl("Confirm Password *")}
                  <input type="password" value={f.confirm} onChange={s("confirm")} placeholder="Repeat password" style={iStyle} required/>
                </div>

                <div style={{gridColumn:"1/-1",borderTop:"1px solid #eaeef5",paddingTop:14,marginTop:4}}>
                  <div style={{fontSize:12,fontWeight:700,color:"#0d1f3c",marginBottom:2}}>🛡️ KYC Verification (Required)</div>
                </div>

                <div>
                  {lbl("PAN Number *")}
                  <input type="text" value={f.pan_number} onChange={upperPan} placeholder="ABCDE1234F" maxLength={10} style={{...iStyle,textTransform:"uppercase"}} required/>
                </div>
                <div>
                  {lbl("Aadhar Number *")}
                  <input type="text" inputMode="numeric" value={f.aadhar_number} onChange={digitsOnlyAadhar} placeholder="12-digit Aadhar number" maxLength={12} style={iStyle} required/>
                </div>

                <UploadField label="PAN Card Upload * (Max 40 KB)" value={f.pan_card} name={f.pan_card_name} onChange={handlePan} hint="Clear photo/scan of your PAN card. JPG/PNG only, under 40 KB."/>
                <UploadField label="Aadhar Card Upload * (Max 40 KB)" value={f.aadhar_card} name={f.aadhar_card_name} onChange={handleAadhar} hint="Clear photo/scan of your Aadhar card. JPG/PNG only, under 40 KB."/>
              </div>
              <button type="submit" style={{width:"100%",padding:13,background:"linear-gradient(135deg,#4a7fb5,#d4736a)",color:"#fff",fontFamily:"'DM Sans',sans-serif",fontSize:14,fontWeight:600,border:"none",borderRadius:10,cursor:"pointer",marginTop:16,boxShadow:"0 4px 14px rgba(74,127,181,0.3)"}}>Register as Agent →</button>
            </form>
            <div style={{marginTop:18,display:"flex",flexDirection:"column",gap:10,alignItems:"center",borderTop:"1px solid #eaeef5",paddingTop:16}}>
              <a onClick={()=>navigate("login")}  style={{fontSize:13,color:"#4a7fb5",cursor:"pointer",textDecoration:"none"}}>← Already have an account? Sign In</a>
              <a onClick={()=>navigate("forgot")} style={{fontSize:13,color:"#4a7fb5",cursor:"pointer",textDecoration:"none"}}>Forgot your password?</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
